import type {
  Layer,
  StorageRecords,
  Stylesheet,
  TagUserLayer,
  UserLayer,
  UserLayerTag,
} from '@onxmaps/layer-management'
import { ref, type UnwrapRef } from 'vue'

const userLayersMap = ref<Map<string, UserLayer>>(new Map())
const userLayerTagsMap = ref<Map<string, UserLayerTag>>(new Map())
const tagsToUserLayerIdsMap = ref<Map<string, Set<string>>>(new Map())

const initialStylesheet = ref<Stylesheet>()
const stylesheet = ref<Stylesheet>()
const shownLayers = ref<Record<string, Layer>>({})
const layerOpacities = ref<Record<string, number>>({})
const layerFilters = ref<Record<string, unknown[]>>({})
const stylesheetSources = ref<Record<string, unknown>>({})
const storage = ref<StorageRecords>({
  layers: {},
  tags: {},
  activities: {},
  difficulties: {},
})

/**
 * *(AI-generated)*
 * Resets all layer management store state to initial values.
 */
export const resetStore = () => {
  userLayersMap.value = new Map()
  userLayerTagsMap.value = new Map()
  tagsToUserLayerIdsMap.value = new Map()
  initialStylesheet.value = undefined
  stylesheet.value = undefined
  shownLayers.value = {}
  layerOpacities.value = {}
  layerFilters.value = {}
  stylesheetSources.value = {}
  storage.value = {
    layers: {},
    tags: {},
    activities: {},
    difficulties: {},
  }
}

/**
 * *(AI-generated)*
 * Returns the layer management store and its update methods.
 */
export const useStore = () => {
  const updateUserLayer = (value: UserLayer) => {
    userLayersMap.value.set(value.id, value.clone())
  }

  const updateUserLayerTag = (value: UserLayerTag) => {
    userLayerTagsMap.value.set(value.id, {
      ...value,
    })
  }

  const updateTagsUserLayerIds = (value: TagUserLayer) => {
    tagsToUserLayerIdsMap.value.set(value.tagId, value.userLayerIds)
  }

  const updateInitialStylesheet = (value: Stylesheet) => {
    initialStylesheet.value = { ...value }
  }

  const updateStylesheet = (value: Stylesheet) => {
    stylesheet.value = {
      ...value,
    }
  }

  const updateStorage = (values: StorageRecords) => {
    storage.value = {
      layers: { ...values.layers },
      tags: { ...values.tags },
      activities: { ...values.activities },
      difficulties: { ...values.difficulties },
    }
  }

  const updateShowLayers = (layers: Layer[]) => {
    const newObj = Object.assign({}, shownLayers.value)
    for (const layer of layers) {
      newObj[layer.id] = layer
    }

    shownLayers.value = newObj
  }

  const updateHideLayers = (layerIds: string[]) => {
    const newObj = Object.assign({}, shownLayers.value)
    for (const layerId of layerIds) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newObj[layerId]
    }

    shownLayers.value = newObj
  }

  const updateLayerOpacities = (value: UnwrapRef<typeof layerOpacities>) => {
    layerOpacities.value = value
  }

  const updateLayerFilters = (value: UnwrapRef<typeof layerFilters>) => {
    layerFilters.value = value
  }

  const updateStylesheetSources = (
    value: UnwrapRef<typeof stylesheetSources>
  ) => {
    stylesheetSources.value = value
  }

  return {
    // Refs
    userLayersMap,
    userLayerTagsMap,
    tagsToUserLayerIdsMap,
    initialStylesheet,
    stylesheet,
    shownLayers,
    layerOpacities,
    layerFilters,
    stylesheetSources,
    storage,

    // Update methods
    updateUserLayer,
    updateUserLayerTag,
    updateTagsUserLayerIds,
    updateInitialStylesheet,
    updateStylesheet,
    updateStorage,
    updateShowLayers,
    updateHideLayers,
    updateLayerOpacities,
    updateLayerFilters,
    updateStylesheetSources,
  }
}
